import { cn } from "@/lib/utils";

interface ColorDotProps {
    color: string;
    label: string;
    size?: "sm" | "md";
}

/** A single color swatch; empty color renders a dashed "not set" placeholder. */
export function ColorDot({ color, label, size = "sm" }: ColorDotProps) {
    const dimension = size === "md" ? "h-8 w-8" : "h-5 w-5";
    if (!color) {
        return (
            <span
                role="img"
                aria-label={`${label}: not set`}
                title={`${label}: not set`}
                className={cn(
                    "inline-block rounded-full border border-dashed border-muted-foreground/50 bg-transparent",
                    dimension,
                )}
            />
        );
    }
    return (
        <span
            role="img"
            aria-label={`${label}: ${color}`}
            title={`${label}: ${color}`}
            className={cn("inline-block rounded-full border border-border/60", dimension)}
            style={{ backgroundColor: color }}
        />
    );
}

interface StyleSwatchProps {
    primaryColor: string;
    secondaryColor: string;
    className?: string;
    size?: "sm" | "md";
}

/** Compact primary+secondary color pair used on cards. */
export function StyleSwatch({ primaryColor, secondaryColor, className, size = "sm" }: StyleSwatchProps) {
    return (
        <div className={cn("flex items-center gap-1", className)}>
            <ColorDot color={primaryColor} label="Primary color" size={size} />
            <ColorDot color={secondaryColor} label="Secondary color" size={size} />
        </div>
    );
}
